import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, RefreshCw, ShieldAlert, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  B2B_OPERATIONS,
  type B2bOperation,
  type B2bWriteMode,
  type B2bWriteModes,
  type CanaryRun,
} from "../../../../../shared/index";

const MODES: B2bWriteMode[] = ["readonly", "dry-run", "live"];

const MODE_HELP: Record<B2bWriteMode, string> = {
  readonly: "Refused. The section still reads.",
  "dry-run": "Runs for real, then rolls back. The diff is recorded and emailed.",
  live: "Commits to the production database.",
};

const OPERATION_HELP: Partial<Record<B2bOperation, string>> = {
  createClient: "Create an API client, with its access, in one transaction",
  updateClient: "Change a client's scalars and scopes",
  setAccess: "Replace a client's endpoint grants and feature flags",
  rotateToken: "Issue a new client token and re-stamp its grants",
  revealToken: "Show an existing token once (recorded, changes nothing)",
  deactivate: "Mark inactive and rotate, so the credentials stop working",
  catalog: "Add or edit an endpoint in the catalog",
  publishDocs: "Publish the docs catalog to the CDN",
  settings: "Change this page",
  revert: "Re-apply a recorded before-state",
};

/**
 * Write modes, notification recipients and propagation health.
 *
 * The write modes are the staged production rollout: an operation moves
 * readonly → dry-run → live one at a time, and this page is where that happens without
 * a deploy.
 */
export default function B2bSettings() {
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ["b2b", "settings"], queryFn: api.b2b.settings });

  const [modes, setModes] = useState<B2bWriteModes | null>(null);
  const [recipients, setRecipients] = useState("");

  useEffect(() => {
    if (settings.data) {
      setModes(settings.data.config.writeModes);
      setRecipients(settings.data.config.notifyTo.join(", "));
    }
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () =>
      api.b2b.saveSettings({
        writeModes: modes ?? undefined,
        notifyTo: recipients
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["b2b"] });
      toast.success("Settings saved");
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : "Could not save settings"),
  });

  const retry = useMutation({
    mutationFn: (clientKey: string) => api.b2b.retryPropagation(clientKey),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["b2b"] });
      toast.success("Eviction retried");
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : "Retry failed"),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">B2B settings</h1>
        <p className="text-sm text-muted-foreground">
          What this section may write, who hears about it, and what has not propagated yet
        </p>
      </div>

      <section className="rounded-xl border bg-card">
        <div className="border-b px-6 py-4">
          <h2 className="font-semibold">Write modes</h2>
          <p className="text-sm text-muted-foreground">
            The External API database is production-only, so each operation is promoted
            separately. Prove it in dry run, verify the canary, then go live.
          </p>
        </div>
        <ul className="divide-y">
          {B2B_OPERATIONS.map((operation) => (
            <li
              key={operation}
              className="flex flex-wrap items-center justify-between gap-3 px-6 py-3"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium">{operation}</div>
                <p className="text-xs text-muted-foreground">{OPERATION_HELP[operation]}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="hidden text-xs text-muted-foreground sm:block">
                  {modes ? MODE_HELP[modes[operation]] : ""}
                </span>
                <select
                  className="h-9 rounded-md border bg-background px-3 text-sm"
                  value={modes?.[operation] ?? "readonly"}
                  onChange={(event) =>
                    setModes((prev) =>
                      prev ? { ...prev, [operation]: event.target.value as B2bWriteMode } : prev
                    )
                  }
                >
                  {MODES.map((mode) => (
                    <option key={mode} value={mode}>
                      {mode}
                    </option>
                  ))}
                </select>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="space-y-4 rounded-xl border bg-card p-6">
        <div>
          <h2 className="font-semibold">Change notifications</h2>
          <p className="text-sm text-muted-foreground">
            Every change emails a redacted diff to these addresses. Tokens are masked.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="recipients">Recipients</Label>
          <Input
            id="recipients"
            value={recipients}
            onChange={(event) => setRecipients(event.target.value)}
            placeholder="owner@tipranks.com, sales@tipranks.com"
          />
          <p className="text-xs text-muted-foreground">Comma separated.</p>
        </div>
      </section>

      <Button onClick={() => save.mutate()} disabled={save.isPending || !modes}>
        {save.isPending ? "Saving…" : "Save settings"}
      </Button>

      <CanarySection canaryKey={settings.data?.config.canaryKey ?? null} tokenSet={settings.data?.config.canaryTokenSet ?? false} lastResult={settings.data?.config.canaryLastResult ?? null} />

      <section className="rounded-xl border bg-card">
        <div className="border-b px-6 py-4">
          <h2 className="font-semibold">Pending cache evictions</h2>
          <p className="text-sm text-muted-foreground">
            A runtime did not confirm the eviction. Until it does, the previous credentials
            may keep working there for up to 24 hours.
          </p>
        </div>
        <ul className="divide-y">
          {(settings.data?.pendingRefresh ?? []).length === 0 && (
            <li className="px-6 py-4 text-sm text-muted-foreground">
              Nothing pending — every change reached both runtimes.
            </li>
          )}
          {(settings.data?.pendingRefresh ?? []).map((entry) => (
            <li
              key={entry.changeId}
              className="flex flex-wrap items-center justify-between gap-3 px-6 py-3"
            >
              <div>
                <code className="text-sm">{entry.clientKey}</code>
                <p className="text-xs text-muted-foreground">
                  {entry.targets.map((target) => (
                    <Badge key={target} variant="destructive" className="mr-1">
                      {target}
                    </Badge>
                  ))}
                  queued {new Date(entry.queuedAt).toLocaleString()}
                </p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => retry.mutate(entry.clientKey)}
                disabled={retry.isPending}
              >
                <RefreshCw className="mr-2 h-3.5 w-3.5" />
                Retry
              </Button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

/**
 * The canary — the only thing that proves a write reached live traffic.
 *
 * Every other safeguard proves we wrote the right row. None of them prove the runtimes
 * noticed: each caches a resolved login for 24 hours, so a correct write can leave a
 * revoked customer working for a day. This calls the live API with real credentials and
 * shows what each runtime answered.
 */
function CanarySection({
  canaryKey,
  tokenSet,
  lastResult,
}: {
  canaryKey: string | null;
  tokenSet: boolean;
  lastResult: CanaryRun | null;
}) {
  const queryClient = useQueryClient();
  const [key, setKey] = useState("");
  const [token, setToken] = useState("");
  const [result, setResult] = useState<CanaryRun | null>(null);

  useEffect(() => setKey(canaryKey ?? ""), [canaryKey]);

  const saveCredentials = useMutation({
    mutationFn: () =>
      api.b2b.saveCanaryCredentials({ key: key.trim() || null, token: token.trim() || null }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["b2b", "settings"] });
      setToken("");
      toast.success("Canary credentials saved. The token is sealed before it is stored.");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const run = useMutation({
    mutationFn: (kind: "check" | "cycle") => api.b2b.runCanary(kind),
    onSuccess: async (canaryRun) => {
      setResult(canaryRun);
      await queryClient.invalidateQueries({ queryKey: ["b2b", "settings"] });
      if (canaryRun.skipped) toast.info(canaryRun.skipped);
      else if (canaryRun.ok) toast.success("Every runtime answered as expected.");
      else toast.error("A runtime did not answer as expected — read the steps below.");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const shown = result ?? lastResult;

  return (
    <section className="rounded-xl border bg-card">
      <div className="border-b px-6 py-4">
        <h2 className="font-semibold">Canary</h2>
        <p className="text-sm text-muted-foreground">
          Calls the live API with the canary&rsquo;s credentials and checks what each runtime
          answers. Run it before promoting any operation to <code>live</code>.
        </p>
      </div>

      <div className="space-y-4 px-6 py-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <Label>Canary Client Key</Label>
            <Input value={key} onChange={(e) => setKey(e.target.value)} placeholder="TR_CANARY" />
          </div>
          <div className="space-y-1">
            <Label>Canary Client Token</Label>
            <Input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder={tokenSet ? "•••• stored — type to replace" : "Paste the token once"}
            />
          </div>
        </div>

        <p className="flex items-start gap-2 text-xs text-muted-foreground">
          <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          The token is encrypted before it is stored and is never sent back to this page.
          Create the canary <strong>through this tool</strong>, never with SQL — a canary
          inserted by hand would prove the tool works using a client the tool did not make.
        </p>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            onClick={() => saveCredentials.mutate()}
            disabled={saveCredentials.isPending}
          >
            Save credentials
          </Button>
          <Button onClick={() => run.mutate("check")} disabled={run.isPending}>
            {run.isPending ? "Running…" : "Run check"}
          </Button>
          <Button
            variant="secondary"
            onClick={() => run.mutate("cycle")}
            disabled={run.isPending}
            title="Revokes the canary's access, proves every runtime refuses it, then restores it"
          >
            Run full cycle
          </Button>
        </div>

        {shown && <CanaryResult run={shown} />}
      </div>
    </section>
  );
}

function CanaryResult({ run }: { run: CanaryRun }) {
  return (
    <div className="space-y-2 rounded-md border p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline">{run.kind}</Badge>
        <Badge variant="outline">mode: {run.mode}</Badge>
        <span className="text-xs text-muted-foreground">
          {new Date(run.at).toLocaleString()} · {run.actor}
        </span>
      </div>

      {run.skipped ? (
        // Not a pass. A run that could not happen must not look like one that did.
        <p className="text-amber-700 dark:text-amber-400">Skipped: {run.skipped}</p>
      ) : (
        <ul className="space-y-1">
          {run.steps.map((step, index) => (
            <li key={`${step.target}-${index}`} className="flex items-start gap-2 text-xs">
              {step.ok ? (
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
              ) : (
                <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
              )}
              <span>
                <strong>{step.target}</strong> — {step.step}: expected{" "}
                {step.expectedStatus.join(" or ")}, got {step.actualStatus ?? "no response"}
                {step.detail ? ` (${step.detail})` : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

